<template>
  <div
    :class="[
      'c-title',
      'title',
      `title--${type}`,
    ]"
  >
    <h3
      v-if="type !== 'tube'"
      v-html="content"
      :data-animation="isAnimated ? 'title' : false"
      :data-lazyanimation="!isAnimated ? 'title' : false"
    />

    <div
      v-if="type === 'tube'"
      class="title__tube-container"
    >
      <h3
        v-for="(content, i) in data.content"
        :key="`title-${i}`"
        :data-animation="i === 0 && isAnimated ? 'title' : false"
        :data-lazyanimation="!isAnimated ? 'title' : false"
      >
        {{ content }}
      </h3>
    </div>
  </div>
</template>

<script>
import anime from 'animejs';
import { SplitText } from '@/assets/libs/SplitText';
import medusa from '@/scripts/medusa';
import { isIOS } from '../scripts/utils';

export default {
  name: 'AppTitle',
  props: {
    type: {
      type: String,
      required: false,
      default: 'plain',
    },
    autoPlay: {
      type: Boolean,
      required: false,
      default: false,
    },
    data: {
      type: Object,
      required: true,
    },
    isAnimated: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      titles: [],
      splitted: [],
      currentIndex: 0,
      handleTubeAnimation: null,
      loopAdded: false,
      idInterval: undefined,
    };
  },
  computed: {
    content() {
      if (this.type === 'marquee') {
        return `
          <span>${this.data.content}</span>
          <span>${this.data.content}</span>
          <span>${this.data.content}</span>
        `;
      }

      return this.data.content;
    },
  },
  mounted() {
    if (this.type === 'tube') {
      this.titles = Array.from(this.$el.querySelectorAll('h3'));
      this.titles.forEach((title, i) => {
        const split = new SplitText(title, { type: 'chars, lines', charsClass: 'title__char', linesClass: 'title__line' });
        this.splitted.push(split);

        if (i !== 0) {
          anime.set(this.splitted[i].chars, { rotateX: -95 });
        }
      });

      medusa.ref.pushToTarget('checkInViewport', this.$el);
      medusa.prevNodes.push(this.$el);

      this.handleTubeAnimation = index => this.tubeAnimation(index);
      if (this.autoPlay) {
        this.startAutoTube();

        // window.addEventListener('blur', () => this.stopAutoTube());
        // window.addEventListener('focus', () => this.startAutoTube());
      }
    }

    if (this.type === 'marquee') {
      const elToAnimate = this.$el.querySelector('h3');
      medusa.ref.pushToTarget('checkInViewport', elToAnimate);
      medusa.prevNodes.push(elToAnimate);
    }
  },
  methods: {
    tubeAnimation(index, goTo = false) {
      this.loopAdded = true;
      if (!this.$el.hasAttribute('data-inviewport')) return;
      if (!this.$el.querySelector('h3').hasAttribute('data-animated')) return;
      if (index === this.currentIndex) return;
      if (this.titles[0] === undefined) return;

      const lineBounding = this.titles[0].querySelector('.title__line').getBoundingClientRect();
      let origin = `50% 50% -${lineBounding.height / 2}px`;
      if (isIOS() || window.safari) {
        origin = '50% 50%';
      }
      const prevIndex = this.currentIndex;
      this.currentIndex = ((goTo ? index : this.currentIndex + 1) % (this.splitted.length));

      anime({
        targets: this.splitted[prevIndex].chars,
        rotateX: [0, prevIndex === 0 ? 95 : 90],
        transformOrigin: [origin, origin],
        delay: anime.stagger(30),
      });
      anime({
        targets: this.splitted[this.currentIndex].chars,
        rotateX: [-90, 0],
        transformOrigin: [origin, origin],
        delay: anime.stagger(30),
      });
    },
    startAutoTube() {
      this.idInterval = setInterval(this.handleTubeAnimation, 2000);
    },
    stopAutoTube() {
      if (!this.loopAdded) return;

      this.loopAdded = false;
      this.idInterval = undefined;
      clearInterval(this.idInterval);
    },
    goTo(i) {
      this.tubeAnimation(i, true);
    },
  },
  beforeDestroy() {
    if (this.loopAdded) clearInterval(this.idInterval);
    this.loopAdded = false;

    if (this.type === 'tube') {
      this.titles = [];
      this.splitted = [];
      this.handleTubeAnimation = null;
    }
  },
};
</script>

<style lang="scss" scoped>
.title {
  position: relative;

  h3 {
    margin: 0;
    min-height: 1px;
  }

  &__tube-container {
    position: relative;

    @include mq(ipadL) {
      white-space: nowrap;
    }

    h3:not(:first-child) {
      position: absolute;
      top: 0;
      left: 0;
    }
  }

  &.title--marquee {
    width: calc(100% + #{$unit-vw * 12});
    left: -$unit-vw * 8;
    overflow: hidden;
    min-height: 20px;

    h3 {
      margin: 0;
      margin-left: $unit-vw * 8;
      transform-style: unset;
      width: 100000px;
      box-sizing: border-box;
      line-height: 1.15;
    }
  }

  @include fluid-type(
    320px,
    1280px,
    40px,
    135px,
    60px,
    175px
  );

  @include mq(ipadL) {
    @include fluid-type(
      1024px,
      3840px,
      56px,
      300px,
      78.4px,
      420px
    );
  }
}
</style>

<style>
@keyframes marquee-text-animation {
  0% { transform: translate3d(0, 0, 0); }
  100% { transform: translate3d(-100%, 0, 0); }
}

.c-title.title.title--marquee h3 span {
  float: left;
  display: block;

  will-change: transform;
  backface-visibility: hidden;
  animation: marquee-text-animation 10s infinite linear;
  animation-play-state: paused;
}

.c-title.title.title--marquee h3[data-inviewport] span {
  animation-play-state: running;
}

.c-title.title.title--plain h3 > div,
.c-title.title.title--tube h3 > div {
  perspective: 500px;
  transform-style: preserve-3d;
  overflow: hidden;
}
</style>
